// Ítem de anuncios de Meta en Mi marca, debajo de Instagram y con sus mismas piezas
// (ADR-2026-09-24-anuncios-de-meta-conexion):
//   • Sin conectar → "Conectar" + modal que explica qué va a ver Atlas.
//   • Con varias cuentas publicitarias → la persona elige una.
//   • Conectada → "Estudio Atlas 2026 · Desconectar".
//   • Vencida → "La conexión venció · Reconectar".
// El estado lo trae `estadoDeAnuncios` (liviano: lee nuestra base, no Meta).

import React from "react";
import { createPortal } from "react-dom";
import { Check, ChevronRight, Loader2, Megaphone, X } from "lucide-react";
import {
  abrirVentanaDeAnuncios,
  conectarAnunciosEnVentana,
  desconectarAnuncios,
  elegirCuentaDeAnuncios,
  ErrorDeAnuncios,
  estadoDeAnuncios,
  nombreDeCuenta,
  type CuentaDeAnuncios,
  type EstadoDeAnuncios,
} from "../lib/metaAdsConexion";
import { ChipDeIntegracion } from "./ChipDeIntegracion";

const ICONO = "h-[18px] w-[18px] shrink-0 text-[#0866FF]";

const MODAL_FONDO = "fixed inset-0 bg-black/50 backdrop-blur-[2px] flex items-center justify-center p-4 z-[80]";
const MODAL_CAJA =
  "bg-surface-card rounded-lg shadow-lg border border-neutral-200 max-w-sm w-full p-[var(--card-padding-mobile)] md:p-[var(--card-padding-desktop)] animate-modal-appear";
const BOTON_SECUNDARIO =
  "h-[var(--btn-h)] px-5 inline-flex items-center justify-center bg-surface-card hover:bg-neutral-100 text-neutral-900 border border-neutral-200 rounded-md font-medium text-[length:var(--text-body)] shadow-sm transition-colors disabled:opacity-50";

export const MetaAdsStatus: React.FC<{
  companyId: string | null;
  brandId: string | null;
  variant?: "chip" | "boton";
}> = ({ companyId, brandId, variant = "chip" }) => {
  const [estado, setEstado] = React.useState<EstadoDeAnuncios | null>(null);
  const [mostrarConectar, setMostrarConectar] = React.useState(false);
  const [mostrarConfirmar, setMostrarConfirmar] = React.useState(false);
  const [conectando, setConectando] = React.useState(false);
  const [desconectando, setDesconectando] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  // Elegir cuenta: el claim sigue en espera en la edge hasta que la persona elija.
  const [eleccion, setEleccion] = React.useState<{ claim: string; cuentas: CuentaDeAnuncios[] } | null>(null);
  const [elegida, setElegida] = React.useState<string | null>(null);
  const marcaActualRef = React.useRef(brandId);
  marcaActualRef.current = brandId;
  const lecturaRef = React.useRef(0);

  /**
   * Lee el estado y lo aplica SÓLO si sigue siendo la marca de ahora y es la última
   * lectura pedida: una vieja que llega tarde no pisa a una nueva (review Greptile #143).
   * Si falla y ya había un estado, se conserva: no se esconde la fila por un tropiezo.
   * Devuelve si se aplicó.
   */
  const leerEstado = async (marca: string | null): Promise<boolean> => {
    const esta = ++lecturaRef.current;
    const nuevo = await estadoDeAnuncios(marca);
    if (marcaActualRef.current !== marca || lecturaRef.current !== esta) return false;
    setEstado((previo) => (nuevo.error && previo ? previo : nuevo));
    return true;
  };

  React.useEffect(() => {
    // Al cambiar de marca se olvida todo lo de la anterior (lección de la review de
    // Greptile en Instagram, #140): nada de la marca vieja queda a la vista ni a un clic.
    setEstado(null);
    setMostrarConectar(false);
    setMostrarConfirmar(false);
    setEleccion(null);
    setElegida(null);
    setError(null);
    if (!companyId) return;
    void leerEstado(brandId);
    // Al cambiar de marca, cualquier lectura en vuelo queda vieja.
    return () => {
      lecturaRef.current++;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, brandId]);

  if (!estado || estado.error) return null;

  const vencida = !estado.conectada && estado.estado === "vencida";
  const nombre = estado.cuenta ? nombreDeCuenta({ nombre: estado.cuenta.nombre ?? "", negocio: estado.cuenta.negocio }) : "";

  const terminarConectada = async (marca: string | null) => {
    await leerEstado(marca);
    // Aunque otra lectura más nueva haya ganado, la conexión terminó: el modal se cierra
    // (salvo que haya cambiado de marca, donde el efecto ya limpió todo).
    if (marcaActualRef.current !== marca) return;
    setMostrarConectar(false);
    setEleccion(null);
    setElegida(null);
  };

  /** Otra persona conectó otra cuenta en la marca mientras tanto: se muestra la que quedó. */
  const refrescarEstado = (marca: string | null) => void leerEstado(marca);

  const conectar = async () => {
    if (conectando) return;
    // ⚠️ La ventana se abre ACÁ, sincrónicamente en el click (Safari/iPhone).
    const popup = abrirVentanaDeAnuncios();
    setConectando(true);
    setError(null);
    const marca = brandId;
    try {
      const r = await conectarAnunciosEnVentana(marca, popup);
      if (marcaActualRef.current !== marca) return;
      if (r.tipo === "elegir") {
        setEleccion({ claim: r.claim, cuentas: r.cuentas });
        setElegida(r.cuentas.find((c) => c.activa)?.id ?? r.cuentas[0]?.id ?? null);
      } else {
        await terminarConectada(marca);
      }
    } catch (e) {
      if (e instanceof ErrorDeAnuncios && e.code === "ventana_cerrada") setError(null);
      else setError(e instanceof Error ? e.message : "No se pudo conectar.");
      if (e instanceof ErrorDeAnuncios && e.code === "cambio_reciente") refrescarEstado(marca);
    } finally {
      setConectando(false);
    }
  };

  const confirmarCuenta = async () => {
    if (!eleccion || !elegida || conectando) return;
    setConectando(true);
    setError(null);
    const marca = brandId;
    try {
      const r = await elegirCuentaDeAnuncios(eleccion.claim, elegida);
      if (marcaActualRef.current !== marca) return;
      if (r.tipo === "conectada") await terminarConectada(marca);
      else setEleccion({ claim: r.claim, cuentas: r.cuentas });
    } catch (e) {
      // Si la cuenta ya está en otra marca, la edge devuelve la lista para elegir otra.
      if (e instanceof ErrorDeAnuncios && e.elegir.length) setEleccion({ claim: eleccion.claim, cuentas: e.elegir });
      else if (e instanceof ErrorDeAnuncios && (/claim_/.test(e.code) || e.code === "cambio_reciente")) {
        setEleccion(null);
        if (e.code === "cambio_reciente") refrescarEstado(marca);
      }
      setError(e instanceof Error ? e.message : "No se pudo conectar.");
    } finally {
      setConectando(false);
    }
  };

  const confirmarDesconectar = async () => {
    if (desconectando) return;
    setDesconectando(true);
    const marca = brandId;
    try {
      await desconectarAnuncios(marca);
      await leerEstado(marca);
      if (marcaActualRef.current !== marca) return;
      setMostrarConfirmar(false);
    } catch {
      /* si falla, queda como estaba */
    } finally {
      setDesconectando(false);
    }
  };

  const abrirConectar = () => {
    setError(null);
    setEleccion(null);
    setMostrarConectar(true);
  };

  const accion = estado.conectada ? (
    <button
      type="button"
      onClick={() => setMostrarConfirmar(true)}
      className="shrink-0 inline-flex items-center gap-1 text-[12.5px] font-medium text-neutral-500 hover:text-neutral-700 transition-colors"
    >
      Desconectar
    </button>
  ) : (
    <button
      type="button"
      onClick={abrirConectar}
      className="shrink-0 inline-flex items-center gap-1 text-[12.5px] font-medium text-brand-primary hover:opacity-70 transition-colors"
    >
      {vencida ? "Reconectar" : "Conectar"}
    </button>
  );

  const subtitulo = estado.conectada ? nombre || "Conectada" : vencida ? "La conexión venció" : null;

  return (
    <>
      {variant === "boton" ? (
        !estado.conectada && !vencida ? (
          <button
            type="button"
            onClick={abrirConectar}
            className="flex w-full items-center gap-2.5 rounded-2xl border border-black/[0.07] bg-white px-3 py-3 text-left transition-opacity active:opacity-70"
          >
            <Megaphone className={ICONO} />
            <span className="flex-1 text-[14px] font-medium text-brand-black/85">Conectar anuncios de Meta</span>
            <ChevronRight className="h-4 w-4 shrink-0 text-black/30" />
          </button>
        ) : (
          <div
            className="flex w-full items-center justify-between gap-2 rounded-2xl border border-black/[0.07] bg-white px-3 py-2.5"
          >
            <div className="flex items-center gap-2.5 min-w-0">
              <Megaphone className={ICONO} />
              <div className="min-w-0">
                <div className="text-[14px] font-medium text-brand-black/80 leading-tight">Anuncios de Meta</div>
                {subtitulo && <div className="text-[12px] text-brand-black/45 truncate leading-tight">{subtitulo}</div>}
              </div>
            </div>
            {accion}
          </div>
        )
      ) : (
        // Chip de la cabecera (Mi marca escritorio) — ADR-2026-10-02-mi-marca-cabecera-de-identidad.
        <ChipDeIntegracion
          clave="meta"
          icono={<Megaphone className="h-4 w-4 shrink-0 text-[#0866FF]" />}
          nombre="Anuncios de Meta"
          estado={estado.conectada ? "conectada" : vencida ? "vencida" : "desconectada"}
          detalle={nombre || null}
          onConectar={abrirConectar}
          onDesconectar={() => setMostrarConfirmar(true)}
        />
      )}

      {/* Modal: conectar y, si hace falta, elegir la cuenta */}
      {mostrarConectar &&
        createPortal(
          <div className={MODAL_FONDO}>
            <div className={MODAL_CAJA}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-[length:var(--text-section-title-mobile)] font-semibold text-neutral-900">
                  {eleccion ? "Elegí la cuenta publicitaria" : vencida ? "Reconectar anuncios" : "Conectar anuncios de Meta"}
                </h3>
                <button
                  type="button"
                  onClick={() => !conectando && setMostrarConectar(false)}
                  className="w-8 h-8 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 transition-colors"
                  aria-label="Cerrar"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {eleccion ? (
                <div className="mb-6 space-y-3 text-[length:var(--text-body)] text-neutral-600">
                  <p>Tu Facebook tiene varias cuentas publicitarias. Elegí la de esta marca:</p>
                  <div className="max-h-64 overflow-y-auto space-y-2" role="radiogroup">
                    {eleccion.cuentas.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        role="radio"
                        aria-checked={elegida === c.id}
                        onClick={() => setElegida(c.id)}
                        className={`w-full flex items-center justify-between gap-2 rounded-lg border px-3 py-2.5 text-left transition-colors ${
                          elegida === c.id ? "border-brand-primary bg-brand-primary/5" : "border-neutral-200 hover:bg-neutral-50"
                        }`}
                      >
                        <span className="min-w-0">
                          <span className="block truncate text-[14px] font-medium text-neutral-900">{c.nombre}</span>
                          <span className="block truncate text-[12px] text-neutral-500">
                            {[c.negocio, c.moneda, c.activa ? null : "inactiva"].filter(Boolean).join(" · ") || `Cuenta ${c.id}`}
                          </span>
                        </span>
                        {elegida === c.id && <Check className="h-4 w-4 shrink-0 text-brand-primary" />}
                      </button>
                    ))}
                  </div>
                  {error && <p className="text-[13px] text-red-600">{error}</p>}
                </div>
              ) : (
                <div className="mb-6 space-y-3 text-[length:var(--text-body)] text-neutral-600">
                  <div className="w-10 h-10 rounded-full bg-neutral-100 flex items-center justify-center mx-auto">
                    <Megaphone className="h-5 w-5 text-[#0866FF]" />
                  </div>
                  <p>
                    Atlas va a poder ver tus campañas de Facebook e Instagram: cuánto gastás, qué resultados
                    traen y cuáles rinden mejor. Así le podés preguntar al chat cómo van tus anuncios.
                  </p>
                  <p>No crea, no pausa ni gasta nada en tu cuenta publicitaria.</p>
                  <p className="text-[13px] text-neutral-500">
                    Se abre una ventana de Facebook para que lo autorices. Dejá marcados todos los permisos.
                  </p>
                  {error && <p className="text-[13px] text-red-600">{error}</p>}
                </div>
              )}

              <div className="flex justify-end gap-3">
                <button type="button" onClick={() => setMostrarConectar(false)} disabled={conectando} className={BOTON_SECUNDARIO}>
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={eleccion ? confirmarCuenta : conectar}
                  disabled={conectando || (!!eleccion && !elegida)}
                  className="h-[var(--btn-h)] px-5 inline-flex items-center justify-center gap-1.5 rounded-md shadow-sm font-medium text-[length:var(--text-body)] transition-colors bg-brand-primary hover:bg-brand-primary-hover text-white disabled:opacity-60"
                >
                  {conectando && <Loader2 className="w-4 h-4 animate-spin" />}
                  {conectando ? "Conectando…" : eleccion ? "Usar esta cuenta" : "Conectar"}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* Modal: confirmar desconexión */}
      {mostrarConfirmar &&
        createPortal(
          <div className={MODAL_FONDO}>
            <div className={MODAL_CAJA}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-[length:var(--text-section-title-mobile)] font-semibold text-neutral-900">
                  Desconectar anuncios
                </h3>
                <button
                  type="button"
                  onClick={() => !desconectando && setMostrarConfirmar(false)}
                  className="w-8 h-8 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 transition-colors"
                  aria-label="Cerrar"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="mb-6">
                <div className="w-10 h-10 rounded-full bg-neutral-100 flex items-center justify-center mx-auto mb-3">
                  <Megaphone className="h-5 w-5 text-[#0866FF]" />
                </div>
                <p className="text-[length:var(--text-body)] text-neutral-600 text-center">
                  ¿Desconectar <span className="font-medium text-neutral-800">{nombre || "tu cuenta publicitaria"}</span> de
                  esta marca? El chat deja de ver tus anuncios. Vas a poder volver a conectarla cuando quieras.
                </p>
              </div>
              <div className="flex justify-end gap-3">
                <button type="button" onClick={() => setMostrarConfirmar(false)} disabled={desconectando} className={BOTON_SECUNDARIO}>
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={confirmarDesconectar}
                  disabled={desconectando}
                  className="h-[var(--btn-h)] px-5 inline-flex items-center justify-center gap-1.5 rounded-md shadow-sm font-medium text-[length:var(--text-body)] transition-colors bg-neutral-900 hover:bg-neutral-800 text-white disabled:opacity-60"
                >
                  {desconectando && <Loader2 className="w-4 h-4 animate-spin" />}
                  {desconectando ? "Desconectando…" : "Desconectar"}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default MetaAdsStatus;
